import { useCallback } from 'react';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';

type UseSecondaryPanelNextNavigationParams = {
  currentPanel: PlannerSecondaryPanelType;
  isCurrentPanelRequired: boolean;
  isCurrentPanelFilled: boolean;
  nextRequiredPanel: PlannerSecondaryPanelType;
  isChange?: boolean;
  confirmButtonLabel?: string;
  onSubmit: () => void | Promise<void>;
  onNavigateNext: (nextPanel: PlannerSecondaryPanelType) => void;
  shouldSkipNextPanel?: (
    currentPanel: PlannerSecondaryPanelType,
    nextRequiredPanel: PlannerSecondaryPanelType,
  ) => boolean;
};

type UseSecondaryPanelNextNavigationResult = {
  handleSubmit: () => Promise<void>;
  submitButtonLabel: string;
  isNextPanelAvailable: boolean;
};

export const useSecondaryPanelNextNavigation = ({
  currentPanel,
  isCurrentPanelRequired,
  isCurrentPanelFilled,
  nextRequiredPanel,
  isChange = false,
  confirmButtonLabel = 'Save',
  onSubmit,
  onNavigateNext,
  shouldSkipNextPanel = () => false,
}: UseSecondaryPanelNextNavigationParams): UseSecondaryPanelNextNavigationResult => {
  const isNextPanelAvailable =
    isCurrentPanelRequired &&
    nextRequiredPanel !== PlannerSecondaryPanelType.NONE &&
    !shouldSkipNextPanel(currentPanel, nextRequiredPanel);

  const isNavigationOnly = isNextPanelAvailable && isCurrentPanelFilled && isChange === false;

  const submitButtonLabel = isNextPanelAvailable ? 'Next' : confirmButtonLabel;

  const handleSubmit = useCallback(async (): Promise<void> => {
    try {
      if (!isNavigationOnly) await onSubmit();
    } catch {
      return;
    }

    if (isNextPanelAvailable) onNavigateNext(nextRequiredPanel);
  }, [isNavigationOnly, isNextPanelAvailable, nextRequiredPanel, onNavigateNext, onSubmit]);

  return {
    handleSubmit,
    submitButtonLabel,
    isNextPanelAvailable,
  };
};
